import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { FlightPayload, GliderPayload, Statistics, CommentPayload, MediaPayload, AdventurePayload } from './types'
import { FlightMapView } from './flview'
import { Button } from '../components/button'
import { ArrowDownTrayIcon, ArrowLeftIcon, ArrowUpTrayIcon, PaperAirplaneIcon, PencilIcon, TrashIcon, PhotoIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon, PlusIcon, EllipsisVerticalIcon, MapIcon, LinkIcon } from '@heroicons/react/20/solid'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../components/dialog'
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu, DropdownDivider } from '../components/dropdown'
import { Combobox, ComboboxOption, ComboboxLabel } from '../components/combobox'
import { FlightPost } from './flight_post'
import { useAuthStatus } from './user_dropdown'
import { PilotAvatar } from './pilot_avatar'
import { getFlightShareUrl, getGliderDisplayName } from './flight_details.helpers'
import { isReplay3DRequested, replay3DSearchParams } from './flview/FlightReplay3D.location'
import { AirtimeMap, Source, Layer, type MapLayerMouseEvent, type MapRef } from '../components/airtime_map'

function InlineTitle({ flightId, initial, fallback, editable }: { flightId: number; initial?: string; fallback: string; editable?: boolean }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(initial ?? '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  const save = async () => {
    setEditing(false)
    const title = value.trim()
    await fetch(`/v0/flight/${flightId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    })
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') save()
    if (e.key === 'Escape') { setValue(initial ?? ''); setEditing(false) }
  }

  if (editing && editable) {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={onKeyDown}
        className="text-2xl font-bold text-[#2D3142] dark:text-white bg-transparent border-b-2 border-[#EF8354] outline-none w-full"
      />
    )
  }

  if (!editable) {
    return (
      <span className="text-2xl font-bold text-[#2D3142] dark:text-white">
        {value.trim() || fallback}
      </span>
    )
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="group flex items-center gap-2 text-left"
    >
      <span className="text-2xl font-bold text-[#2D3142] dark:text-white">
        {value.trim() || fallback}
      </span>
      <PencilIcon className="h-4 w-4 text-[#4F5D75] dark:text-[#BFC0C0] opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  )
}

const FLIGHT_TYPES = ['site flight', 'hike and fly', 'tandem'] as const

function InlineType({ flightId, initial, onChange, editable }: { flightId: number; initial: string; onChange?: (v: string) => void; editable?: boolean }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(initial)

  const save = async (next: string) => {
    setValue(next)
    setEditing(false)
    onChange?.(next)
    await fetch(`/v0/flight/${flightId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: next }),
    })
  }

  if (editing && editable) {
    return (
      <span className="inline-flex gap-1">
        {FLIGHT_TYPES.map(t => (
          <button
            key={t}
            onClick={() => save(t)}
            className={`rounded px-2 py-0.5 text-sm font-medium transition-colors ${
              t === value
                ? 'bg-[#EF8354] text-white'
                : 'bg-[#BFC0C0]/20 dark:bg-[#4F5D75]/20 text-[#4F5D75] dark:text-[#BFC0C0] hover:bg-[#EF8354]/20'
            }`}
          >
            {t}
          </button>
        ))}
        <button onClick={() => setEditing(false)} className="text-xs text-[#4F5D75] dark:text-[#BFC0C0] px-1">✕</button>
      </span>
    )
  }

  if (!editable) {
    return <span className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">{value}</span>
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="group inline-flex items-center gap-1 text-left"
    >
      <span className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">{value}</span>
      <PencilIcon className="h-3 w-3 text-[#4F5D75] dark:text-[#BFC0C0] opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  )
}

function InlineGlider({
  flightId,
  initialGliderId,
  initialGliderName,
  onChange,
  editable,
}: {
  flightId: number
  initialGliderId?: string
  initialGliderName?: string
  onChange?: (gliderId: string | null) => void
  editable?: boolean
}) {
  const [gliders, setGliders] = useState<GliderPayload[]>([])
  const [editing, setEditing] = useState(false)
  const [gliderId, setGliderId] = useState(initialGliderId ?? '')
  const selectRef = useRef<HTMLSelectElement>(null)

  useEffect(() => {
    if (editable) {
      fetch('/v0/gliders').then(r => r.json()).then(d => setGliders(d.gliders ?? []))
    }
  }, [editable])

  useEffect(() => {
    if (editing) setTimeout(() => selectRef.current?.focus(), 0)
  }, [editing])

  const save = async (nextId: string) => {
    setGliderId(nextId)
    setEditing(false)
    const gliderID = nextId || null
    onChange?.(gliderID)
    await fetch(`/v0/flight/${flightId}/glider`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ glider_id: gliderID }),
    })
  }

  const current = gliders.find(g => g.id === gliderId)
  const displayName = getGliderDisplayName(gliderId || undefined, initialGliderName, gliders)

  if (!editable) {
    if (!displayName) return null
    return <span className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">{displayName}</span>
  }

  const label = displayName ?? '+ add glider'

  if (editing) {
    return (
      <select
        ref={selectRef}
        value={gliderId}
        onChange={e => save(e.target.value)}
        onBlur={() => setEditing(false)}
        className="rounded border border-[#EF8354] bg-white dark:bg-[#2D3142] px-1 py-0 text-sm text-[#2D3142] dark:text-white outline-none"
      >
        <option value="">— no glider —</option>
        {gliders.map(g => (
          <option key={g.id} value={g.id}>
            {g.name}{g.brand ? ` (${g.brand})` : ''}
          </option>
        ))}
      </select>
    )
  }

  if (gliders.length === 0) return null

  return (
    <button
      onClick={() => setEditing(true)}
      className="group inline-flex items-center gap-1 text-left"
    >
      <span className={`text-sm ${current ? 'text-[#4F5D75] dark:text-[#BFC0C0]' : 'text-[#EF8354] hover:underline'}`}>
        {label}
      </span>
      {current && (
        <PencilIcon className="h-3 w-3 text-[#4F5D75] dark:text-[#BFC0C0] opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </button>
  )
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-4 py-1">
      <span className="w-40 shrink-0 text-sm text-[#4F5D75] dark:text-[#BFC0C0]">{label}</span>
      <span className="text-sm font-medium text-[#2D3142] dark:text-white">{value}</span>
    </div>
  )
}

function StatSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4 first:mt-0">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#EF8354]">{title}</p>
      {children}
    </div>
  )
}

function fmtUpTrackDuration(ns: number): string {
  const totalMin = Math.round(ns / 60_000_000_000)
  if (totalMin <= 0) return '< 1 min'
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return h > 0 ? `${h}h ${m}m` : `${m} min`
}

function StatisticsPanel({ stats }: { stats: Statistics }) {
  return (
    <div className="mt-2 rounded-md bg-[#BFC0C0]/10 dark:bg-[#4F5D75]/10 px-3 py-3">
      <StatSection title="Flight">
        <StatRow label="Duration"        value={stats.duration} />
        <StatRow label="Distance"        value={stats.scores?.['FAI'] != null ? `${stats.scores['FAI'].toFixed(1)} km` : '—'} />
        <StatRow label="Points"          value={stats.scores?.['XContest'] != null ? `${stats.scores['XContest'].toFixed(1)}` : '—'} />
      </StatSection>
      <StatSection title="Altitude">
        <StatRow label="Top altitude" value={`${stats.top_altitude} m`} />
        <StatRow label="Max gain"     value={`${stats.max_gain} m`} />
      </StatSection>
      <StatSection title="Speed">
        <StatRow label="Average speed"  value={`${stats.average_speed_kmh.toFixed(1)} km/h`} />
        <StatRow label="Max speed"      value={`${stats.max_speed_kmh.toFixed(1)} km/h`} />
        <StatRow label="Max climb rate" value={`${stats.max_climb_rate.toFixed(1)} m/s`} />
      </StatSection>
      {stats.up_track && (
        <StatSection title="Hike up">
          <StatRow label="Distance"       value={`${(stats.up_track.distance_m / 1000).toFixed(1)} km`} />
          <StatRow label="Duration"       value={fmtUpTrackDuration(stats.up_track.duration_ns)} />
          <StatRow label="Elevation gain" value={`${Math.round(stats.up_track.elevation_gain_m)} m`} />
        </StatSection>
      )}
    </div>
  )
}

interface TakeoffEntry {
  id: number
  name: string
  coordinates: string
  lat: number
  lon: number
}

interface MapBounds {
  north: number
  south: number
  east: number
  west: number
}

function parseTakeoffCoords(t: { id: number; name: string; coordinates: string }): TakeoffEntry | null {
  const parts = t.coordinates.split(',')
  if (parts.length !== 2) return null
  const lat = parseFloat(parts[0].trim())
  const lon = parseFloat(parts[1].trim())
  if (isNaN(lat) || isNaN(lon)) return null
  return { ...t, lat, lon }
}

function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function isInBounds(t: TakeoffEntry, b: MapBounds): boolean {
  return t.lat >= b.south && t.lat <= b.north && t.lon >= b.west && t.lon <= b.east
}

function takeoffsToGeoJSON(entries: TakeoffEntry[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: entries.map(t => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [t.lon, t.lat] },
      properties: { id: t.id, name: t.name },
    })),
  }
}

function CreateTakeoffModal({
  open,
  onClose,
  flightId,
  initialLat,
  initialLon,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  flightId: number
  initialLat: number
  initialLon: number
  onCreated: (name: string, coords: string, takeoffId: number) => void
}) {
  const [lat, setLat] = useState(initialLat)
  const [lon, setLon] = useState(initialLon)
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [allTakeoffs, setAllTakeoffs] = useState<TakeoffEntry[]>([])
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)
  const mapRef = useRef<MapRef>(null)

  useEffect(() => {
    if (open) {
      setLat(initialLat)
      setLon(initialLon)
      setName('')
      setMapBounds(null)
      setTimeout(() => nameRef.current?.focus(), 100)
      fetch('/v0/takeoffs')
        .then(r => r.json())
        .then(data => {
          const list: TakeoffEntry[] = (data.takeoffs ?? [])
            .map((t: { id: number; name: string; coordinates: string }) => parseTakeoffCoords(t))
            .filter((t: TakeoffEntry | null): t is TakeoffEntry => t !== null)
          setAllTakeoffs(list)
        })
        .catch(() => setAllTakeoffs([]))
    }
  }, [open, initialLat, initialLon])

  // Takeoffs visible in the current map viewport, sorted by distance from flight start
  const visibleTakeoffs = useMemo(() => {
    if (!mapBounds) return []
    return allTakeoffs
      .filter(t => isInBounds(t, mapBounds))
      .map(t => ({ ...t, distanceM: haversineMeters(initialLat, initialLon, t.lat, t.lon) }))
      .sort((a, b) => a.distanceM - b.distanceM)
  }, [allTakeoffs, mapBounds, initialLat, initialLon])

  // GeoJSON for all takeoff markers on the map
  const geojson = useMemo(() => takeoffsToGeoJSON(allTakeoffs), [allTakeoffs])

  const pointData = {
    type: 'Feature' as const,
    geometry: { type: 'Point' as const, coordinates: [lon, lat] },
    properties: {},
  }

  const updateBounds = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    const b = map.getBounds()
    if (!b) return
    setMapBounds({
      north: b.getNorth(),
      south: b.getSouth(),
      east: b.getEast(),
      west: b.getWest(),
    })
  }, [])

  const linkExisting = async (t: TakeoffEntry) => {
    setSaving(true)
    try {
      await fetch(`/v0/flight/${flightId}/takeoff`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ takeoff_id: t.id, takeoff_name: t.name, coordinates: t.coordinates }),
      })
      onCreated(t.name, t.coordinates, t.id)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  const handleMapClick = useCallback((e: MapLayerMouseEvent) => {
    // If a takeoff marker was clicked, link it directly
    const features = e.features
    if (features && features.length > 0) {
      const props = features[0].properties
      if (props?.id) {
        const t = allTakeoffs.find(tk => tk.id === props.id)
        if (t) {
          linkExisting(t)
          return
        }
      }
    }
    // Otherwise set the pin for creating a new takeoff
    setLat(e.lngLat.lat)
    setLon(e.lngLat.lng)
  }, [allTakeoffs]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      const coords = `${lat.toFixed(6)},${lon.toFixed(6)}`

      const createRes = await fetch('/v0/takeoffs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), coordinates: coords }),
      })
      if (!createRes.ok) return
      const { id: takeoffId } = await createRes.json()

      await fetch(`/v0/flight/${flightId}/takeoff`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ takeoff_id: takeoffId, takeoff_name: name.trim(), coordinates: coords }),
      })

      onCreated(name.trim(), coords, takeoffId)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Set Takeoff</DialogTitle>
      <DialogDescription>
        Pick a visible takeoff or click on the map to create a new one.
      </DialogDescription>
      <DialogBody>
        <div className="h-72 rounded-lg overflow-hidden mb-3">
          <AirtimeMap
            ref={mapRef}
            initialViewState={{ longitude: initialLon, latitude: initialLat, zoom: 13 }}
            style={{ width: '100%', height: '100%' }}
            onClick={handleMapClick}
            onLoad={updateBounds}
            onMoveEnd={updateBounds}
            interactiveLayerIds={['takeoff-markers']}
            cursor="crosshair"
          >
            {/* Existing takeoff markers */}
            <Source id="takeoffs-src" type="geojson" data={geojson}>
              <Layer
                id="takeoff-markers"
                type="circle"
                paint={{
                  'circle-radius': 6,
                  'circle-color': '#EF8354',
                  'circle-stroke-color': '#ffffff',
                  'circle-stroke-width': 1.5,
                }}
              />
            </Source>
            {/* New takeoff pin */}
            <Source id="new-takeoff-src" type="geojson" data={pointData}>
              <Layer
                id="new-takeoff-circle"
                type="circle"
                paint={{
                  'circle-radius': 10,
                  'circle-color': '#EF8354',
                  'circle-stroke-color': '#ffffff',
                  'circle-stroke-width': 2,
                }}
              />
            </Source>
          </AirtimeMap>
        </div>
        <p className="mb-3 text-xs text-[#4F5D75] dark:text-[#BFC0C0]">
          {lat.toFixed(6)}, {lon.toFixed(6)}
        </p>
        {visibleTakeoffs.length > 0 && (
          <div className="mb-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#EF8354]">
              Takeoffs on map
              <span className="ml-1 text-[#4F5D75] dark:text-[#BFC0C0] font-normal">({visibleTakeoffs.length})</span>
            </p>
            <ul className="divide-y divide-[#BFC0C0]/20 dark:divide-[#4F5D75]/20 rounded-md border border-[#BFC0C0]/30 dark:border-[#4F5D75]/30 max-h-40 overflow-y-auto">
              {visibleTakeoffs.map(t => (
                <li key={t.id} className="flex items-center justify-between px-3 py-2">
                  <div>
                    <span className="text-sm font-medium text-[#2D3142] dark:text-white">{t.name}</span>
                    <span className="ml-2 text-xs text-[#4F5D75] dark:text-[#BFC0C0]">
                      {t.distanceM < 1000 ? `${Math.round(t.distanceM)} m` : `${(t.distanceM / 1000).toFixed(1)} km`}
                    </span>
                  </div>
                  <Button plain onClick={() => linkExisting(t)} disabled={saving}>Use</Button>
                </li>
              ))}
            </ul>
            <p className="mt-3 mb-1 text-xs font-semibold uppercase tracking-wider text-[#EF8354]">Or create new</p>
          </div>
        )}
        <input
          ref={nameRef}
          type="text"
          placeholder="Takeoff name"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSubmit() }}
          className="w-full rounded-md border border-[#BFC0C0]/50 dark:border-[#4F5D75]/50 bg-transparent px-3 py-2 text-sm text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] transition-colors"
        />
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={saving}>Cancel</Button>
        <Button color="airtime" onClick={handleSubmit} disabled={!name.trim() || saving}>
          {saving ? 'Saving…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function PhotoSection({ flightId, isOwner }: { flightId: number; isOwner: boolean }) {
  const [photos, setPhotos] = useState<MediaPayload[]>([])
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    fetch(`/v0/flight/${flightId}/photos`)
      .then(r => r.json())
      .then(d => setPhotos(d.photos ?? []))
      .catch(() => {})
  }, [flightId])

  const uploadFile = useCallback(async (file: File) => {
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await fetch(`/v0/flight/${flightId}/photos`, { method: 'POST', body: form })
      if (!res.ok) return
      const data = await res.json()
      setPhotos(prev => [...prev, data])
    } finally {
      setUploading(false)
    }
  }, [flightId])

  const handleFiles = (files: FileList | null) => {
    if (!files) return
    Array.from(files).forEach(f => { if (f.type.startsWith('image/')) uploadFile(f) })
  }

  const deletePhoto = async (id: string) => {
    await fetch(`/v0/media/${id}`, { method: 'DELETE' })
    setPhotos(prev => prev.filter(p => p.id !== id))
    if (lightboxIndex !== null) {
      const newPhotos = photos.filter(p => p.id !== id)
      if (newPhotos.length === 0) setLightboxIndex(null)
      else setLightboxIndex(i => i !== null ? Math.min(i, newPhotos.length - 1) : null)
    }
  }

  const prev = () => setLightboxIndex(i => i !== null ? (i - 1 + photos.length) % photos.length : null)
  const next = () => setLightboxIndex(i => i !== null ? (i + 1) % photos.length : null)

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if (lightboxIndex === null) return
    if (e.key === 'ArrowLeft') prev()
    if (e.key === 'ArrowRight') next()
    if (e.key === 'Escape') setLightboxIndex(null)
  }, [lightboxIndex, photos.length])

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onKeyDown])

  const currentPhoto = lightboxIndex !== null ? photos[lightboxIndex] : null

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#EF8354]">Photos</p>
        {isOwner && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0] hover:text-[#EF8354] dark:hover:text-[#EF8354] border border-[#BFC0C0]/40 dark:border-[#4F5D75]/40 hover:border-[#EF8354] transition-colors disabled:opacity-40"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            {uploading ? 'Uploading…' : 'Add photo'}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={e => handleFiles(e.target.files)}
        />
      </div>

      {photos.length === 0 && !isOwner ? null : (
        <div
          onDragOver={isOwner ? (e => { e.preventDefault(); setDragOver(true) }) : undefined}
          onDragLeave={isOwner ? (() => setDragOver(false)) : undefined}
          onDrop={isOwner ? (e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files) }) : undefined}
          className={`transition-colors rounded-lg ${dragOver ? 'bg-[#EF8354]/10 ring-2 ring-[#EF8354]/40' : ''}`}
        >
          {photos.length === 0 ? (
            isOwner && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex flex-col items-center justify-center gap-2 py-8 rounded-lg border-2 border-dashed border-[#BFC0C0]/40 dark:border-[#4F5D75]/40 text-[#4F5D75] dark:text-[#BFC0C0] hover:border-[#EF8354]/60 hover:text-[#EF8354] transition-colors"
              >
                <PhotoIcon className="h-8 w-8 opacity-40" />
                <span className="text-sm">Drop photos here or click to upload</span>
              </button>
            )
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {photos.map((photo, i) => (
                <button
                  key={photo.id}
                  onClick={() => setLightboxIndex(i)}
                  className="group relative aspect-square overflow-hidden rounded-lg bg-[#BFC0C0]/10 dark:bg-[#4F5D75]/10 hover:ring-2 hover:ring-[#EF8354] transition-all"
                >
                  <img
                    src={`/v0/media/${photo.id}`}
                    alt={photo.filename}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {currentPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={() => setLightboxIndex(null)}
        >
          {/* Close */}
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
          >
            <XMarkIcon className="h-8 w-8" />
          </button>

          {/* Delete (own photos only — server enforces ownership) */}
          {isOwner && (
            <button
              onClick={e => { e.stopPropagation(); deletePhoto(currentPhoto.id) }}
              className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-md bg-black/50 px-3 py-1.5 text-sm text-red-400 hover:text-red-300 hover:bg-black/70 transition-colors"
            >
              <TrashIcon className="h-4 w-4" />
              Delete
            </button>
          )}

          {/* Prev */}
          {photos.length > 1 && (
            <button
              onClick={e => { e.stopPropagation(); prev() }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors"
            >
              <ChevronLeftIcon className="h-10 w-10" />
            </button>
          )}

          {/* Image */}
          <img
            src={`/v0/media/${currentPhoto.id}`}
            alt={currentPhoto.filename}
            className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-2xl object-contain"
            onClick={e => e.stopPropagation()}
          />

          {/* Next */}
          {photos.length > 1 && (
            <button
              onClick={e => { e.stopPropagation(); next() }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors"
            >
              <ChevronRightIcon className="h-10 w-10" />
            </button>
          )}

          {/* Counter */}
          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-white/60">
            {(lightboxIndex ?? 0) + 1} / {photos.length}
          </p>
        </div>
      )}
    </div>
  )
}

function CommentsSection({ flightId }: { flightId: number }) {
  const { userData } = useAuthStatus()
  const [comments, setComments] = useState<CommentPayload[]>([])
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    fetch(`/v0/flight/${flightId}/comments`)
      .then(r => r.json())
      .then(d => setComments(d.comments ?? []))
      .catch(() => {})
  }, [flightId])

  const submit = async () => {
    const trimmed = body.trim()
    if (!trimmed) return
    setSubmitting(true)
    try {
      await fetch(`/v0/flight/${flightId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: trimmed }),
      })
      const updated = await fetch(`/v0/flight/${flightId}/comments`).then(r => r.json())
      setComments(updated.comments ?? [])
      setBody('')
    } finally {
      setSubmitting(false)
    }
  }

  const deleteComment = async (commentId: number) => {
    await fetch(`/v0/flight/${flightId}/comments/${commentId}`, { method: 'DELETE' })
    setComments(prev => prev.filter(c => c.id !== commentId))
  }

  const formatCommentDate = (s: string) =>
    new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) +
    ' at ' +
    new Date(s).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })

  return (
    <div className="mt-8">
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-[#EF8354]">Comments</p>
      {comments.length === 0 ? (
        <p className="text-sm text-center text-[#4F5D75] dark:text-[#BFC0C0] mb-4">No comments yet.</p>
      ) : (
        <ul className="space-y-4 mb-6">
          {comments.map(c => (
            <li key={c.id} className="group flex gap-3">
              <PilotAvatar
                pilot={{ name: c.pilot_name, avatar: c.pilot_avatar }}
                className="size-8 shrink-0 bg-[#4F5D75]/20 text-[#4F5D75] dark:bg-[#BFC0C0]/20 dark:text-[#BFC0C0]"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-[#2D3142] dark:text-white">{c.pilot_name || c.pilot_nick}</span>
                  <span className="text-xs text-[#4F5D75] dark:text-[#BFC0C0]">{formatCommentDate(c.created_at)}</span>
                  {userData.pilot?.nick === c.pilot_nick && (
                    <button
                      onClick={() => deleteComment(c.id)}
                      className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-[#4F5D75] dark:text-[#BFC0C0] hover:text-red-500 dark:hover:text-red-400"
                      aria-label="Delete comment"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <p className="mt-0.5 text-sm text-[#2D3142] dark:text-[#BFC0C0] whitespace-pre-wrap">{c.body}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
      {userData.authenticated && (
        <div className="flex gap-3">
          <PilotAvatar
            pilot={userData.pilot}
            className="size-8 shrink-0 bg-[#4F5D75]/20 text-[#4F5D75] dark:bg-[#BFC0C0]/20 dark:text-[#BFC0C0]"
          />
          <div className="flex-1">
            <textarea
              ref={textareaRef}
              value={body}
              onChange={e => setBody(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit() }}
              placeholder="Add a comment…"
              rows={2}
              className="w-full rounded-lg border border-[#BFC0C0]/40 dark:border-[#4F5D75]/40 bg-white dark:bg-[#2D3142] px-3 py-2 text-sm text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] resize-none transition-colors"
            />
            <div className="mt-1.5 flex justify-end">
              <button
                onClick={submit}
                disabled={!body.trim() || submitting}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#EF8354] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#EF8354]/80 disabled:opacity-40 transition-colors"
              >
                <PaperAirplaneIcon className="h-3.5 w-3.5" />
                {submitting ? 'Posting…' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export function FlightDetails() {
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const replay3D = isReplay3DRequested(searchParams)
  const setReplay3D = useCallback((open: boolean) => {
    setSearchParams(current => replay3DSearchParams(current, open))
  }, [setSearchParams])
  const navigate = useNavigate()
  const [flight, setFlight] = useState<FlightPayload | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createTakeoffOpen, setCreateTakeoffOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [uploadingUpTrack, setUploadingUpTrack] = useState(false)
  const upTrackInputRef = useRef<HTMLInputElement>(null)
  const { userData } = useAuthStatus()
  const [adventureModalOpen, setAdventureModalOpen] = useState(false)
  const [allAdventures, setAllAdventures] = useState<AdventurePayload[]>([])
  const [selectedAdventure, setSelectedAdventure] = useState<AdventurePayload | null>(null)
  const [attachingAdventure, setAttachingAdventure] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleUpTrackUpload = useCallback(async (file: File) => {
    if (!flight) return
    setUploadingUpTrack(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await fetch(`/v0/flight/${flight.id}/up-track`, { method: 'PUT', body: form })
      if (!res.ok) return
      // Refetch flight data
      const flightRes = await fetch(`/v0/flight/${flight.id}`)
      const updated = await flightRes.json()
      setFlight(updated)
      // Then merge track data
      try {
        const tracksRes = await fetch(`/v0/flight/${flight.id}/tracks`)
        if (tracksRes.ok) {
          const tracksData = await tracksRes.json()
          setFlight(prev => {
            if (!prev) return prev
            return {
              ...prev,
              json_track: tracksData.json_track ?? prev.json_track ?? [],
              json_up_track: tracksData.json_up_track ?? prev.json_up_track ?? [],
            }
          })
        }
      } catch {
        // Tracks unavailable — flight still displays without map
      }
    } finally {
      setUploadingUpTrack(false)
    }
  }, [flight])

  const openAdventureModal = useCallback(async () => {
    const res = await fetch('/v0/adventures')
    if (res.ok) {
      const data = await res.json()
      setAllAdventures(data.adventures ?? [])
    }
    setSelectedAdventure(null)
    setAdventureModalOpen(true)
  }, [])

  const handleAttachAdventure = useCallback(async () => {
    if (!selectedAdventure || !flight) return
    setAttachingAdventure(true)
    try {
      const res = await fetch(`/v0/adventure/${selectedAdventure.id}/flights/${flight.id}`, { method: 'PUT' })
      if (res.ok) {
        setFlight(prev => prev ? { ...prev, adventure_id: selectedAdventure.id } : prev)
        setAdventureModalOpen(false)
      }
    } finally {
      setAttachingAdventure(false)
    }
  }, [selectedAdventure, flight])

  const handleDetachAdventure = useCallback(async () => {
    if (!flight?.adventure_id) return
    const res = await fetch(`/v0/adventure/${flight.adventure_id}/flights/${flight.id}`, { method: 'DELETE' })
    if (res.ok) {
      setFlight(prev => prev ? { ...prev, adventure_id: undefined } : prev)
    }
  }, [flight])

  const copyLink = useCallback(async () => {
    if (!flight) return
    const url = getFlightShareUrl(flight.id, window.location.origin)
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access unavailable — nothing to copy.
    }
  }, [flight])

  useEffect(() => {
    if (!id) return
    let cancelled = false

    const load = async () => {
      try {
        setIsLoading(true)
        const flightRes = await fetch(`/v0/flight/${id}`)
        if (!flightRes.ok) {
          throw new Error('Failed to fetch flight details')
        }
        const data = await flightRes.json()
        if (cancelled) return
        setFlight(data)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'An error occurred')
        return
      } finally {
        if (!cancelled) setIsLoading(false)
      }

      // Load tracks separately so the flight UI renders immediately while
      // the (potentially large) track payload is still in flight.
      try {
        const tracksRes = await fetch(`/v0/flight/${id}/tracks`)
        if (!tracksRes.ok || cancelled) return
        const tracksData = await tracksRes.json()
        if (cancelled) return
        setFlight(prev => {
          if (!prev) return prev
          return {
            ...prev,
            json_track: tracksData.json_track ?? prev.json_track ?? [],
            json_up_track: tracksData.json_up_track ?? prev.json_up_track ?? [],
          }
        })
      } catch {
        // Tracks unavailable — the flight still displays without the map.
      }
    }

    load()
    return () => { cancelled = true }
  }, [id])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#1A1C2E]">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="animate-pulse">
          <div className="h-8 bg-[#BFC0C0] rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-[#BFC0C0] rounded w-1/2 mb-8"></div>
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-16 bg-[#BFC0C0] rounded"></div>
            ))}
          </div>
        </div>
        </div>
      </div>
    )
  }

  if (error || !flight) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#1A1C2E]">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Button plain onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}>
          <ArrowLeftIcon />
          Back
        </Button>
        <div className="mt-8 text-center">
          <h2 className="text-lg font-semibold text-[#2D3142] dark:text-white">Flight Not Found</h2>
          <p className="mt-2 text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
            {error || 'The flight you are looking for does not exist.'}
          </p>
        </div>
        </div>
      </div>
    )
  }

  const isOwner = !!userData.pilot && userData.pilot.id === flight.pilot_id

  const deleteFlight = async () => {
    setDeleting(true)
    try {
      await fetch(`/v0/flight/${flight.id}`, { method: 'DELETE' })
      navigate('/')
    } finally {
      setDeleting(false)
    }
  }

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <>
    <div className="min-h-screen bg-gray-50 dark:bg-[#1A1C2E]">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button plain onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}>
            <ArrowLeftIcon />
            Back
          </Button>
          {isOwner && (flight.prev_flight_id || flight.next_flight_id) && (
            <div className="flex items-center gap-1 ml-2">
              <Button
                plain
                disabled={!flight.prev_flight_id}
                onClick={() => flight.prev_flight_id && navigate(`/flight/${flight.prev_flight_id}`)}
                aria-label="Previous flight"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </Button>
              <Button
                plain
                disabled={!flight.next_flight_id}
                onClick={() => flight.next_flight_id && navigate(`/flight/${flight.next_flight_id}`)}
                aria-label="Next flight"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </Button>
            </div>
          )}
        </div>
        {isOwner && (
          <Dropdown>
            <DropdownButton plain aria-label="Flight actions">
              <EllipsisVerticalIcon className="h-5 w-5 text-[#4F5D75] dark:text-[#BFC0C0]" />
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              <DropdownItem onClick={copyLink}>
                <LinkIcon className="h-4 w-4" />
                <DropdownLabel>Copy link</DropdownLabel>
              </DropdownItem>
              <DropdownItem onClick={() => upTrackInputRef.current?.click()} disabled={uploadingUpTrack}>
                <ArrowUpTrayIcon className="h-4 w-4" />
                <DropdownLabel>{uploadingUpTrack ? 'Uploading…' : flight.json_up_track?.length ? 'Replace hike-up track' : 'Add hike-up track'}</DropdownLabel>
              </DropdownItem>
              {flight.adventure_id ? (
                <DropdownItem onClick={handleDetachAdventure}>
                  <XMarkIcon className="h-4 w-4" />
                  <DropdownLabel>Remove from adventure</DropdownLabel>
                </DropdownItem>
              ) : (
                <DropdownItem onClick={openAdventureModal}>
                  <MapIcon className="h-4 w-4" />
                  <DropdownLabel>Add to adventure</DropdownLabel>
                </DropdownItem>
              )}
              <DropdownDivider />
              <DropdownItem onClick={() => setDeleteOpen(true)}>
                <TrashIcon className="h-4 w-4 text-red-500" />
                <DropdownLabel className="text-red-500">Delete flight</DropdownLabel>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        )}
        <input
          ref={upTrackInputRef}
          type="file"
          accept=".gpx"
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0]
            if (file) handleUpTrackUpload(file)
            e.target.value = ''
          }}
        />
      </div>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete flight?</DialogTitle>
        <DialogDescription>
          This will permanently delete the flight, all its comments, and any attached photos. This cannot be undone.
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <button
            onClick={deleteFlight}
            disabled={deleting}
            className="inline-flex items-center rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-40 transition-colors"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </DialogActions>
      </Dialog>

      <Dialog open={adventureModalOpen} onClose={() => setAdventureModalOpen(false)}>
        <DialogTitle>Add to Adventure</DialogTitle>
        <DialogDescription>Choose an adventure to attach this flight to.</DialogDescription>
        <DialogBody>
          <Combobox
            options={allAdventures}
            value={selectedAdventure ?? undefined}
            onChange={v => setSelectedAdventure(v ?? null)}
            displayValue={a => a?.title}
            placeholder="Search adventures…"
          >
            {a => (
              <ComboboxOption value={a}>
                <ComboboxLabel>{a.title}</ComboboxLabel>
              </ComboboxOption>
            )}
          </Combobox>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setAdventureModalOpen(false)} disabled={attachingAdventure}>Cancel</Button>
          <Button color="airtime" onClick={handleAttachAdventure} disabled={!selectedAdventure || attachingAdventure}>
            {attachingAdventure ? 'Adding…' : 'Add to Adventure'}
          </Button>
        </DialogActions>
      </Dialog>

      <div className="mb-8">
        {flight.pilot_nick && (
          <div className="flex items-center gap-3 mb-3">
            <Link to={`/pilot/${flight.pilot_nick}`}>
              <PilotAvatar
                pilot={{ name: flight.pilot_name, avatar: flight.pilot_avatar, profile_picture_id: flight.pilot_profile_picture_id }}
                className="size-10 bg-[#4F5D75]/20 text-[#4F5D75] dark:bg-[#BFC0C0]/20 dark:text-[#BFC0C0]"
              />
            </Link>
            <Link
              to={`/pilot/${flight.pilot_nick}`}
              className="text-sm font-medium text-[#2D3142] dark:text-white hover:text-[#EF8354] dark:hover:text-[#EF8354] transition-colors"
            >
              {flight.pilot_name || flight.pilot_nick}
            </Link>
          </div>
        )}
        <InlineTitle
          flightId={flight.id}
          initial={flight.title}
          fallback={flight.takeoff_name && flight.takeoff_name !== 'unknown' ? `${flight.type} at ${flight.takeoff_name}` : flight.type}
          editable={isOwner}
        />
        <p className="mt-2 text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
          <InlineType flightId={flight.id} initial={flight.type} editable={isOwner} onChange={t => setFlight(prev => prev ? { ...prev, type: t } : prev)} /> at{' '}
          {flight.takeoff_id && flight.takeoff_name !== 'unknown' ? (
            <span className="group/takeoff inline-flex items-center gap-1">
              <Link
                to={`/takeoff/${encodeURIComponent(flight.takeoff_name)}`}
                className="text-[#EF8354] hover:underline"
              >
                {flight.takeoff_name}
              </Link>
              {userData.authenticated && (
                <button
                  onClick={() => setCreateTakeoffOpen(true)}
                  className="opacity-0 group-hover/takeoff:opacity-100 transition-opacity"
                  aria-label="Change takeoff"
                >
                  <PencilIcon className="h-3 w-3 text-[#4F5D75] dark:text-[#BFC0C0]" />
                </button>
              )}
            </span>
          ) : (
            userData.authenticated ? (
              <button
                onClick={() => setCreateTakeoffOpen(true)}
                className="text-[#EF8354] hover:underline"
              >
                + add takeoff
              </button>
            ) : (
              <span className="text-[#4F5D75] dark:text-[#BFC0C0]">{flight.takeoff_name || 'unknown'}</span>
            )
          )}
          {' '}on {formatDate(flight.start_time)} {formatTime(flight.start_time)}
          {' · '}
          <InlineGlider
            flightId={flight.id}
            initialGliderId={flight.glider_id}
            initialGliderName={flight.glider_name}
            editable={isOwner}
            onChange={gliderId => setFlight(prev => prev ? { ...prev, glider_id: gliderId ?? undefined } : prev)}
          />
          {flight.adventure_id && (
            <>
              {' · '}
              <Link
                to={`/adventure/${flight.adventure_id}`}
                className="inline-flex items-center gap-1 text-[#EF8354] hover:underline"
              >
                <MapIcon className="h-3.5 w-3.5 inline" />
                Adventure
              </Link>
            </>
          )}
        </p>
      </div>

      {((flight.json_track && flight.json_track.length > 0) || (flight.json_up_track && flight.json_up_track.length > 0)) && (
        <div className="mb-8 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 flex flex-col">
            <FlightMapView flight={flight} replay3D={replay3D} onReplay3DChange={setReplay3D} />
          </div>

          {flight.statistics && (
            <div className="lg:w-80 flex flex-col bg-white dark:bg-[#2D3142] shadow-lg rounded-lg">
              <div className="px-6 py-4 flex-1 overflow-y-auto">
                <StatisticsPanel stats={flight.statistics} />
              </div>
              {(flight.igc_url || flight.gpx_url) && (
                <div className="px-6 py-3 border-t border-[#BFC0C0]/20 dark:border-[#4F5D75]/20 flex flex-wrap gap-2">
                  {flight.igc_url && (
                    <a
                      href={flight.igc_url}
                      download
                      className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0] hover:text-[#EF8354] dark:hover:text-[#EF8354] border border-[#BFC0C0]/40 dark:border-[#4F5D75]/40 hover:border-[#EF8354] transition-colors"
                    >
                      <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                      IGC track
                    </a>
                  )}
                  {flight.gpx_url && (
                    <a
                      href={flight.gpx_url}
                      download
                      className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-[#4F5D75] dark:text-[#BFC0C0] hover:text-[#EF8354] dark:hover:text-[#EF8354] border border-[#BFC0C0]/40 dark:border-[#4F5D75]/40 hover:border-[#EF8354] transition-colors"
                    >
                      <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                      GPX hike-up
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isOwner ? (
        <FlightPost flightId={flight.id} initial={flight.post} />
      ) : flight.post ? (
        <div className="mb-8">
          <div className="bg-white dark:bg-[#2D3142] shadow-lg rounded-lg overflow-hidden">
            <div
              className="prose prose-sm dark:prose-invert max-w-none px-4 py-3"
              dangerouslySetInnerHTML={{ __html: flight.post }}
            />
          </div>
        </div>
      ) : null}

      <PhotoSection flightId={flight.id} isOwner={isOwner} />

      <CommentsSection flightId={flight.id} />

      <CreateTakeoffModal
        open={createTakeoffOpen}
        onClose={() => setCreateTakeoffOpen(false)}
        flightId={flight.id}
        initialLat={flight.json_track?.[0]?.coordinates.lat ?? (flight.takeoff_coords ? parseFloat(flight.takeoff_coords.split(',')[0]) : 46.5197)}
        initialLon={flight.json_track?.[0]?.coordinates.lon ?? (flight.takeoff_coords ? parseFloat(flight.takeoff_coords.split(',')[1]) : 6.6323)}
        onCreated={(name, coords, takeoffId) =>
          setFlight(prev => prev
            ? { ...prev, takeoff_name: name, takeoff_coords: coords, takeoff_id: takeoffId }
            : prev)
        }
      />

      </div>
    </div>

    {copied && (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 pointer-events-none rounded-lg bg-[#2D3142] px-4 py-2 text-sm font-medium text-white shadow-lg">
        Link copied!
      </div>
    )}

    </>
  )
}
